import { z } from "zod";
import {
  AgentTaskStatus,
  AgentWorkflowRunTaskStatus,
  AgentWorkflowRunTaskType,
} from "./agents";
import { CustomFieldFilterParamSchema } from "./customFields";

export const TASK_METADATA_ENTITY_TYPE_VALUES = ["QUOTE"] as const;
export const TaskMetadataEntityTypeSchema = z.enum(
  TASK_METADATA_ENTITY_TYPE_VALUES,
);
export type TaskMetadataEntityType =
  (typeof TASK_METADATA_ENTITY_TYPE_VALUES)[number];
export const TASK_METADATA_ENTITY_TYPE = TaskMetadataEntityTypeSchema.enum;

export const AgentWorkflowRunTaskMetadataSchema = z.object({
  actionName: z.string().optional(),
  entityId: z.string().optional(),
  entityType: TaskMetadataEntityTypeSchema.optional(),
  actionInput: z.record(z.string(), z.unknown()).optional(),
  /**
   * True when an agent-typed owner was supplied at task creation, so the
   * hydrated owners list includes the owning thread's agent alongside any
   * human assignees.
   */
  agentOwner: z.boolean().optional(),
});
export type AgentWorkflowRunTaskMetadata = z.infer<
  typeof AgentWorkflowRunTaskMetadataSchema
>;

/**
 * A task owner. `userId` points at an existing user, `email` is resolved to
 * a user at write time (and rejected if no match), and `agent` means the
 * currently executing agent owns the task on its own behalf.
 */
export const TaskOwnerSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("userId"), id: z.string() }),
  z.object({ type: z.literal("email"), email: z.string().email() }),
  z.object({ type: z.literal("agent") }),
]);
export type TaskOwner = z.infer<typeof TaskOwnerSchema>;

/**
 * Hydrated owner shape returned to clients — userId-typed owners come with
 * display fields, email-typed owners always resolve to a userId before being
 * stored, so the response shape is always userId-or-agent. Agent owners
 * identify the owning thread via its agentSdkThreadId.
 */
export const HydratedTaskOwnerSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("userId"),
    id: z.string(),
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
  }),
  z.object({ type: z.literal("agent"), agentSdkThreadId: z.string() }),
]);
export type HydratedTaskOwner = z.infer<typeof HydratedTaskOwnerSchema>;

/** Every action a task can run on approval. */
export const TASK_ACTION_ID = {
  FINALIZE_QUOTE: "FinalizeQuote",
  PREVIEW_QUOTE: "PreviewQuote",
  SEND_EMAIL: "SendEmail",
} as const;

/** An `actionId` currently supported as a task action. */
export type TaskActionId = (typeof TASK_ACTION_ID)[keyof typeof TASK_ACTION_ID];

/** Every `actionId` currently supported as a task action. */
export const TASK_ACTION_IDS: TaskActionId[] = Object.values(TASK_ACTION_ID);

/**
 * Parameters for the `FinalizeQuote` task action. Extend this union to add
 * new action types.
 */
export const FinalizeQuoteActionParametersSchema = z.object({
  actionId: z.literal(TASK_ACTION_ID.FINALIZE_QUOTE),
  parameters: z.object({ quoteId: z.string() }),
});

/**
 * Parameters for the `PreviewQuote` task action. Opens the same review
 * experience as FinalizeQuote but performs no state transition on approval.
 */
export const PreviewQuoteActionParametersSchema = z.object({
  actionId: z.literal(TASK_ACTION_ID.PREVIEW_QUOTE),
  parameters: z.object({ quoteId: z.string() }),
});

/**
 * The email a `SendEmail` task sends on approval, as the agent asked for it with
 * `email_send_action`. Approval resolves the sending connection and the thread's
 * recipients when it sends.
 */
export const SendEmailRequestSchema = z.object({
  replyToMessageId: z.string().optional(),
  forwardMessageId: z.string().optional(),
  recipients: z.array(z.string()).optional(),
  cc: z.array(z.string()).optional(),
  bcc: z.array(z.string()).optional(),
  replyAll: z.boolean().optional(),
  subject: z.string().optional(),
  content: z.string().optional(),
  contentType: z.enum(["text", "html"]).optional(),
  assetIds: z.array(z.string()).optional(),
});
export type SendEmailRequest = z.infer<typeof SendEmailRequestSchema>;

/** Parameters for the `SendEmail` task action: the email approval sends. */
export const SendEmailActionParametersSchema = z.object({
  actionId: z.literal(TASK_ACTION_ID.SEND_EMAIL),
  parameters: SendEmailRequestSchema,
});
export type SendEmailActionParameters = z.infer<
  typeof SendEmailActionParametersSchema
>;

/**
 * Discriminated union of every action that may be attached to a task. The
 * tool that creates a task declares the action via `actionParameters` and the
 * approve flow executes the matching server-side handler synchronously.
 */
export const TaskActionParametersSchema = z.discriminatedUnion("actionId", [
  FinalizeQuoteActionParametersSchema,
  PreviewQuoteActionParametersSchema,
  SendEmailActionParametersSchema,
]);
export type TaskActionParameters = z.infer<typeof TaskActionParametersSchema>;

/**
 * Full task payload returned by `get_task` and the messages endpoint.
 * Description is markdown — hint that at the edges (tool hints, rendering
 * layer) rather than encoding it in the field name.
 */
export const AgentTaskSchema = z.object({
  id: z.string(),
  threadId: z.string().nullable(),
  threadName: z.string().nullable().optional(),
  opportunityId: z.string().nullable(),
  type: z.nativeEnum(AgentWorkflowRunTaskType),
  status: z.nativeEnum(AgentTaskStatus),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  actionParameters: TaskActionParametersSchema.nullable(),
  blockedByTaskIds: z.array(z.string()),
  assetIds: z.array(z.string()),
  owners: z.array(HydratedTaskOwnerSchema),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AgentTask = z.infer<typeof AgentTaskSchema>;

/**
 * Compact task projection returned by `list_tasks` — strips the description
 * to keep the response small.
 */
export const AgentTaskSummarySchema = AgentTaskSchema.omit({
  description: true,
});
export type AgentTaskSummary = z.infer<typeof AgentTaskSummarySchema>;

/**
 * Request body for creating a quote review task on a thread from the UI. Takes
 * the same `actionParameters` shape as the agent's `create_or_update_task`
 * tool. SendEmail tasks are only created by the agent, with its own owners.
 */
export const CreateThreadTaskRequestSchema = z.object({
  actionParameters: z.discriminatedUnion("actionId", [
    FinalizeQuoteActionParametersSchema,
    PreviewQuoteActionParametersSchema,
  ]),
});
export type CreateThreadTaskRequest = z.infer<
  typeof CreateThreadTaskRequestSchema
>;

/**
 * Response for the create-thread-task endpoint. Carries the task that the
 * thread panel should open, whether it was just created or already open.
 */
export const CreateThreadTaskResponseSchema = z.object({
  task: AgentTaskSchema,
});
export type CreateThreadTaskResponse = z.infer<
  typeof CreateThreadTaskResponseSchema
>;

/** Re-export task type enum so consumers can reach it via this module. */
export { AgentWorkflowRunTaskType };

/** An assignee surfaced on an inbox item so the UI can show ownership and gate actions. */
export const InboxItemAssigneeSchema = z.object({
  userId: z.string(),
  name: z.string(),
});
export type InboxItemAssignee = z.infer<typeof InboxItemAssigneeSchema>;

export const WorkflowReviewInboxItemSchema = z.object({
  sourceType: z.literal("workflow_run_review"),
  taskId: z.string(),
  status: z.nativeEnum(AgentWorkflowRunTaskStatus),
  updatedAt: z.coerce.date(),
  actionParameters: TaskActionParametersSchema.nullable(),
  runId: z.string(),
  runName: z.string().nullable(),
  agentWorkflowId: z.string(),
  agentWorkflowName: z.string(),
  subject: z.string().nullable(),
  opportunityId: z.string().nullable(),
  opportunityName: z.string().nullable(),
  stageName: z.string().nullable(),
  assignees: z.array(InboxItemAssigneeSchema),
});

export type WorkflowReviewInboxItem = z.infer<
  typeof WorkflowReviewInboxItemSchema
>;

export const AgentSdkReviewInboxItemSchema = z.object({
  sourceType: z.literal("agent_sdk_review"),
  taskId: z.string(),
  status: z.nativeEnum(AgentWorkflowRunTaskStatus),
  updatedAt: z.coerce.date(),
  actionParameters: TaskActionParametersSchema.nullable(),
  threadId: z.string(),
  messageId: z.string().nullable(),
  threadName: z.string().nullable(),
  agentId: z.string(),
  subject: z.string().nullable(),
  description: z.string().nullable(),
  opportunityId: z.string().nullable(),
  opportunityName: z.string().nullable(),
  stageName: z.string().nullable(),
  assignees: z.array(InboxItemAssigneeSchema),
});

export type AgentSdkReviewInboxItem = z.infer<
  typeof AgentSdkReviewInboxItemSchema
>;

export const PendingInboxItemSchema = z.union([
  WorkflowReviewInboxItemSchema,
  AgentSdkReviewInboxItemSchema,
]);

export type PendingInboxItem = z.infer<typeof PendingInboxItemSchema>;

export const PendingInboxItemsResponseSchema = z.object({
  items: z.array(PendingInboxItemSchema),
});

export type PendingInboxItemsResponse = z.infer<
  typeof PendingInboxItemsResponseSchema
>;

/** Scope selector for the inbox: the user's own items or the whole organization. */
export const InboxScopeEnum = z.enum(["USER", "ORGANIZATION"]);
export type InboxScope = z.infer<typeof InboxScopeEnum>;

/** Filters for querying inbox items: scope selector plus organization-scope predicates. */
export const InboxFilterSchema = z.object({
  scope: InboxScopeEnum.catch("USER"),
  assigneeUserId: z.string().optional(),
  opportunityId: z.string().optional(),
  customFields: CustomFieldFilterParamSchema.optional(),
});
export type InboxFilter = z.infer<typeof InboxFilterSchema>;

/** Filter for querying agent tasks scoped to an opportunity. */
export const TaskFilterSchema = z.object({
  statuses: z.array(z.nativeEnum(AgentTaskStatus)).optional(),
});
export type TaskFilter = z.infer<typeof TaskFilterSchema>;

/**
 * Response schema for listing agent tasks scoped to an opportunity.
 */
export const ListOpportunityTasksResponseSchema = z.object({
  tasks: z.array(AgentTaskSchema),
});
export type ListOpportunityTasksResponse = z.infer<
  typeof ListOpportunityTasksResponseSchema
>;
